import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { AttachmentChipView } from './AttachmentChipView';

/**
 * An inline chip standing for a file attached to the document.
 *
 * Inline and atomic on purpose: the point of the feature is writing notes
 * AROUND a file, so the chip has to sit inside a paragraph of prose rather
 * than interrupting it as a block, and it must not be partially selectable.
 *
 * The node stores only an id plus enough to render a label offline. The bytes
 * are fetched on demand by the preview modal, because /api/attachments/* is
 * auth-gated and a plain href cannot send the header (see CLAUDE.md).
 */
export interface AttachmentChipOptions {
  HTMLAttributes: Record<string, unknown>;
}

export const AttachmentChipNode = Node.create<AttachmentChipOptions>({
  name: 'attachmentChip',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,
  draggable: true,

  addOptions() {
    return { HTMLAttributes: {} };
  },

  addAttributes() {
    return {
      attachmentId: {
        default: null,
        parseHTML: (el) => el.getAttribute('data-attachment-id'),
        renderHTML: (attrs) =>
          attrs.attachmentId ? { 'data-attachment-id': attrs.attachmentId } : {},
      },
      filename: {
        default: '',
        parseHTML: (el) => el.getAttribute('data-filename') ?? '',
        renderHTML: (attrs) => (attrs.filename ? { 'data-filename': attrs.filename } : {}),
      },
      mimeType: {
        default: '',
        parseHTML: (el) => el.getAttribute('data-mime-type') ?? '',
        renderHTML: (attrs) => (attrs.mimeType ? { 'data-mime-type': attrs.mimeType } : {}),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'span[data-type="attachment-chip"]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        'data-type': 'attachment-chip',
        class: 'attachment-chip-node',
      }),
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(AttachmentChipView);
  },
});
